import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createQueryParams } from '../helpers';
import { DialogService } from './dialog.service';
import { NotificationService } from './notifications.service';
import { getApiError } from '../components/warehouse/warehouse.utils';
import {
  NpCabinet,
  NpCabinetCreatePayload,
  NpCabinetUpdatePayload,
  NpCity,
  NpDocument,
  NpLinkPayload,
  NpPrintFormat,
  NpShipment,
  NpShipmentEstimate,
  NpShipmentPayload,
  NpWarehouse,
  NpWarehouseKind,
} from '../components/nova-poshta/nova-poshta.types';

const NP_API = 'application/nova-poshta';
const NP_TRACKING_URL = 'https://novaposhta.ua/tracking/';

/**
 * Nova Poshta of CRM 2.0: cabinets of the company, the city and branch lookup and the waybills of deals, plus the
 * feedback of its screens. Every request needs the installed application, else 403 APPLICATION_NOT_INSTALLED, which
 * logs the user out: the screens are reached only with the application installed.
 */
@Injectable({ providedIn: 'root' })
export class BoardNovaPoshtaService {
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  // #region cabinets

  /** The default cabinet first */
  readonly getCabinets = (): Observable<NpCabinet[]> => this.request('GET', 'cabinets');
  /** Checks the key with Nova Poshta and reads the sender of the cabinet; 400 NP_INVALID_API_KEY, 409 NP_CABINET_DUPLICATE_KEY */
  readonly createCabinet = (body: NpCabinetCreatePayload): Observable<NpCabinet> =>
    this.request('POST', 'cabinets', body);
  /** Without `api_key` the saved one stays; null clears the place of sending */
  readonly updateCabinet = (id: string, body: NpCabinetUpdatePayload): Observable<NpCabinet> =>
    this.request('PUT', `cabinets/${id}`, body);
  /** The waybills stay, the oldest cabinet becomes the default one */
  readonly deleteCabinet = (id: string): Observable<void> => this.request('DELETE', `cabinets/${id}`);

  // #endregion

  // #region address book

  /** Up to 20 settlements with branches; an empty search gives none */
  readonly searchCities = (search: string): Observable<NpCity[]> => this.request('GET', 'cities', null, { search });
  /** Up to 50 branches of the city */
  readonly getWarehouses = (
    cityRef: string,
    search: string,
    kind: NpWarehouseKind | 'not_postomat',
  ): Observable<NpWarehouse[]> => this.request('GET', `cities/${cityRef}/warehouses`, null, { search, kind });

  // #endregion

  // #region waybills

  /** Checks only what the price depends on, so a half-filled form goes as it is; 400 NP_SENDER_NOT_SET */
  readonly estimate = (body: NpShipmentPayload): Observable<NpShipmentEstimate> =>
    this.request('POST', 'estimate', body);

  /** The newest first; the statuses older than 10 minutes are read from Nova Poshta again */
  readonly getCardShipments = (cardId: string): Observable<NpShipment[]> =>
    this.request('GET', `cards/${cardId}/shipments`);
  /** Writes the number to the `ttn_number` field of the deal, the card update comes by the socket */
  readonly createShipment = (cardId: string, body: NpShipmentPayload): Observable<NpShipment> =>
    this.request('POST', `cards/${cardId}/shipments`, body);
  /** Deletes the waybill in Nova Poshta too: only a `created` one, else 409 NP_SHIPMENT_ALREADY_SENT */
  readonly deleteShipment = (id: string): Observable<void> => this.request('DELETE', `shipments/${id}`);

  /** Waybills of the cabinet of the last 30 days; `search` is the number, the recipient phone or name */
  readonly getCabinetDocuments = (cabinetId: string, search: string): Observable<NpDocument[]> =>
    this.request('GET', `cabinets/${cabinetId}/documents`, null, { search });
  /** One waybill by its number; 404 NP_SHIPMENT_NOT_FOUND */
  readonly findDocument = (cabinetId: string, ttn: string): Observable<NpDocument> =>
    this.request('GET', `documents/${ttn.replace(/\s/g, '')}`, null, { cabinet_id: cabinetId });
  /** Linking it again to the same deal returns it; 409 NP_SHIPMENT_ALREADY_LINKED when another deal has it */
  readonly linkShipment = (cardId: string, body: NpLinkPayload): Observable<NpShipment> =>
    this.request('POST', `cards/${cardId}/shipments/link`, body);
  /** The waybill leaves the deal and stays in Nova Poshta */
  readonly unlinkShipment = (id: string): Observable<void> => this.request('DELETE', `shipments/${id}/link`);

  /**
   * Opens the PDF of the waybill in a new tab. The tab is opened right away, before the request, or the browser
   * blocks it as a popup.
   */
  print(shipment: NpShipment, format: NpPrintFormat): void {
    const tab = window.open('', '_blank');
    this.http
      .get(`${environment.baseURL}/${NP_API}/shipments/${shipment.id}/print`, {
        params: createQueryParams({ format }),
        responseType: 'blob',
      })
      .subscribe({
        next: (pdf) => {
          const url = URL.createObjectURL(pdf);
          if (tab) tab.location.href = url;
          else window.open(url, '_blank');
          // The tab has the file by then
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        error: async (error) => {
          tab?.close();
          this.error(await this.readBlobError(error));
        },
      });
  }

  trackingUrl(ttn: string): string {
    return `${NP_TRACKING_URL}${ttn}`;
  }

  // #endregion

  // #region feedback

  t(key: string, params?: Record<string, unknown>): string {
    return String(this.translateService.instant(key, params));
  }

  success(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showSuccess('', this.t(key, params));
  }

  /** Toast of an API error */
  error(error: unknown): void {
    this.notificationService.showError('', this.errorMessage(error));
  }

  getErrorCode(error: unknown): string | undefined {
    return getApiError(error).code;
  }

  /**
   * `novaposhta.errors.<CODE>`, the default error for a code without a text. A refusal of Nova Poshta comes with its
   * own texts, shown as they are.
   */
  errorMessage(error: unknown): string {
    const { code, details } = getApiError(error);
    const npErrors = Array.isArray(details?.['errors']) ? (details['errors'] as unknown[]).map(String) : [];
    if (code === 'NP_REQUEST_FAILED' && npErrors.length) return npErrors.join('<br>');

    const key = `novaposhta.errors.${code}`;
    // `details` fill the text, e.g. the deal that has the waybill already
    const text = code ? this.t(key, details) : key;
    return text === key ? this.t('novaposhta.errors.DEFAULT') : text;
  }

  /** Emits once the translated question is confirmed */
  confirm(key: string, params?: Record<string, unknown>): Observable<unknown> {
    return this.dialogService.openConfirm({ text: this.t(key, params), isTranslate: true }).pipe(filter(Boolean));
  }

  // #endregion

  /** A response is `{ data }`; a DELETE has none */
  private request<T>(method: string, path: string, body?: unknown, query?: object): Observable<T> {
    return this.http
      .request<{ data: T } | null>(method, `${environment.baseURL}/${NP_API}/${path}`, {
        body,
        params: query && createQueryParams(query),
      })
      .pipe(map((res) => res?.data as T));
  }

  /** The error of a blob request has its JSON body as a blob too */
  private async readBlobError(error: unknown): Promise<unknown> {
    const body = (error as { error?: unknown } | null)?.error;
    if (!(body instanceof Blob)) return error;
    try {
      return { error: JSON.parse(await body.text()) };
    } catch {
      return error;
    }
  }
}
